// * api
import type { DownloadableEntity } from "~/aihd-base-nuxt/types/components/download-photo";
import { fetchDownloadNormalLink, getUpscalePhotoApi } from "../api";
import { usePusherEvents } from "~/composables/usePusherEvent";

// * composables

export const useDownloadPhoto = () => {
    const { openSnackbar } = useSnackbar();

    // * pusher
    const { upscaleEvent, channel, unbindUpscale } = usePusherEvents();

    // * states
    const isDownloading = useState<boolean>("isDownloading", () => false);
    const downloadableEntity = useState<DownloadableEntity>("downloadableEntity");
    const timeoutInterval = useState<any>("timeoutInterval");
    const timerTick = useState<number>("timerTick", () => 0);
    const hasVirtuallyStagingLabel = useState("hasVirtuallyStagingLabel", () => false);
    const hasCustomLogo = useState("hasCustomLogo", () => false);
    const showPromotionModal = useState("showPromotionModal", () => false);

    // * methods
    const downloadResultNormally = async (): Promise<any> => {
        isDownloading.value = true;

        const { data, error } = await fetchDownloadNormalLink(downloadableEntity.value.id, {
            to_be_labeled: hasVirtuallyStagingLabel.value,
            custom_logo: hasCustomLogo.value,
        });

        isDownloading.value = false;
        hasVirtuallyStagingLabel.value = false;
        hasCustomLogo.value = false;
        if (data.value) {
            return downloadPhotoFromUrl(data.value?.src);
        }
        openSnackbar({
            description: error.value?.message,
            state: "error",
        });
    };

    const downloadResultUpscaled = async () => {
        isDownloading.value = true;

        // initPusher();
        const { data, error } = await getUpscalePhotoApi(downloadableEntity.value.id as string, {
            to_be_labeled: hasVirtuallyStagingLabel.value,
            custom_logo: hasCustomLogo.value,
        });
        hasVirtuallyStagingLabel.value = false;
        hasCustomLogo.value = false;

        if (error.value) {
            isDownloading.value = false;
            // ! temp static error message
            openSnackbar({
                description: "Upscale Failed, Please Retry",
                state: "error",
            });
        }

        if (data.value.status === "processing") {
            timeoutInterval.value = setInterval(() => {
                timerTick.value++;
                if (timerTick.value === 60 && channel.value) {
                    timerTick.value = 0;
                    // abortPusher();
                    clearInterval(timeoutInterval.value);
                    openSnackbar({
                        description: "Timeout has occurred. Please retry.",
                        state: "error",
                    });
                    isDownloading.value = false;
                }
            }, 1000);

            // * function should be called when the upscale image is ready
            const afterUpscaleEvent = async (response: { message: string; download_src: string }) => {
                if (response) {
                    getUpscaledPhoto(response.download_src);
                    clearInterval(timeoutInterval.value);
                    unbindUpscale();
                }
            };

            // ! listen to upscale events
            upscaleEvent(afterUpscaleEvent);
        } else if (data.value.status === "available") {
            downloadPhotoFromUrl(data.value.src as string);
            isDownloading.value = false;
        }
    };

    watch(channel, () => {
        if (!channel.value) isDownloading.value = false;
    });

    const getUpscaledPhoto = async (src: string) => {
        downloadPhotoFromUrl(src);
        isDownloading.value = false;
    };

    return {
        isDownloading,
        hasVirtuallyStagingLabel,
        downloadableEntity,
        timeoutInterval,
        showPromotionModal,
        hasCustomLogo,
        timerTick,
        downloadResultUpscaled,
        downloadResultNormally,
    };
};
